import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { Sidebar } from "./components/Sidebar";
import { DashboardPage } from "./pages/Dashboard";
import { InvoicesPage, InvoiceDetailPage } from "./pages/Invoices";
import { PaymentsPage, AccountPage } from "./pages/Payments";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/Auth";
import {
  AdminLoginPage, AdminDashboardPage, AdminCustomersPage,
  AdminCustomerDetailPage, AdminDisputesPage, AdminTicketsPage
} from "./pages/Admin";
import { useState, useEffect } from "react";
import { api } from "./api";
import { NotificationBell } from "./components/NotificationBell";
import { SuperAdminPage } from "./pages/SuperAdminPage";
import { AdminChangePasswordPage } from "./pages/AdminChangePassword";
import { AdminResetPasswordPage } from "./pages/AdminResetPassword";
import "./index.css";

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/admin/login" replace />;
}

function PreviewBanner() {
  const isPreview = localStorage.getItem("ar_preview_mode") === "true";
  const customerName = localStorage.getItem("ar_preview_customer");
  if (!isPreview) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: "#CC2229", color: "white", textAlign: "center",
      padding: "8px 16px", fontSize: "0.875rem", fontWeight: 500,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 16,
    }}>
      <span>👁 ADMIN PREVIEW — Viewing as: {customerName} — Actions are disabled in preview mode</span>
      <button
        onClick={() => {
          localStorage.removeItem("ar_preview_mode");
          localStorage.removeItem("ar_preview_customer");
          localStorage.removeItem("ar_token");
          localStorage.removeItem("ar_email");
          if (!window.close() && window.opener) window.close();
          else window.location.href = "/admin";
        }}
        style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "white", padding: "3px 10px", borderRadius: 4, cursor: "pointer", fontSize: "0.8rem" }}
      >
        Exit Preview
      </button>
    </div>
  );
}

function AppShell({ children }) {
  const [adminStats, setAdminStats] = useState(null);
  const { isAdmin, isAuthenticated } = useAuth();
  const isPreview = localStorage.getItem("ar_preview_mode") === "true";

  useEffect(() => {
    if (isAdmin) {
      api.adminDashboard().then(setAdminStats).catch(() => {});
    }
  }, [isAdmin]);

  return (
    <>
      {isPreview && <PreviewBanner />}
      <div className="app-shell" style={isPreview ? { marginTop: 40 } : {}}>
        <Sidebar adminStats={adminStats} />
        <main className="main-content">
          {isAuthenticated && !isAdmin && !isPreview && (
            <div className="no-print" style={{ position: "fixed", top: 16, right: 20, zIndex: 400 }}>
              <NotificationBell />
            </div>
          )}
          {children}
        </main>
      </div>
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public customer auth */}
      <Route path="/login"           element={<LoginPage />} />
      <Route path="/register"        element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password"  element={<ResetPasswordPage />} />

      {/* Admin auth */}
      <Route path="/admin/login" element={<AdminLoginPage />} />

      {/* Protected customer routes */}
      <Route path="/" element={<ProtectedRoute><AppShell><DashboardPage /></AppShell></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><AppShell><InvoicesPage /></AppShell></ProtectedRoute>} />
      <Route path="/invoices/:id" element={<ProtectedRoute><AppShell><InvoiceDetailPage /></AppShell></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><AppShell><PaymentsPage /></AppShell></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AppShell><AccountPage /></AppShell></ProtectedRoute>} />

      {/* Protected admin routes */}
      <Route path="/admin" element={<AdminRoute><AppShell><AdminDashboardPage /></AppShell></AdminRoute>} />
      <Route path="/admin/customers" element={<AdminRoute><AppShell><AdminCustomersPage /></AppShell></AdminRoute>} />
      <Route path="/admin/customers/:id" element={<AdminRoute><AppShell><AdminCustomerDetailPage /></AppShell></AdminRoute>} />
      <Route path="/admin/disputes" element={<AdminRoute><AppShell><AdminDisputesPage /></AppShell></AdminRoute>} />
      <Route path="/admin/tickets" element={<AdminRoute><AppShell><AdminTicketsPage /></AppShell></AdminRoute>} />

      <Route path="/admin/super" element={<AdminRoute><AppShell><SuperAdminPage /></AppShell></AdminRoute>} />
      <Route path="/admin/change-password" element={<AdminChangePasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
